/**
 * Searchable model picker for an OpenCode LLM configuration.
 *
 * OpenCode supports ~230 providers and ~9k models, far past what a <select>
 * can present, so the choice is made here:
 *
 *  - "OpenCode providers" mode (auth `configured`): ONE searchable list of the
 *    models the providers configured in OpenCode on the server expose
 *    (OpenCode Zen's free models included).
 *  - "API key" mode (bring your own key): providers on the left (search, "only
 *    with a saved key"), the chosen provider's models on the right (search),
 *    and that provider's KEY inline — save / replace / remove / test. Keys are
 *    write-only: the server only ever says whether one is stored.
 *
 * Rendered as its own overlay rather than a nested <Modal>: the shared Modal
 * closes on a document-level Escape listener, so a nested one would also close
 * the configuration editor underneath and lose its unsaved edits. This overlay
 * swallows Escape in the capture phase and closes only itself.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, X, Check, KeyRound, Server, ExternalLink } from 'lucide-react'
import toast from 'react-hot-toast'
import Button from '@/components/common/Button'
import {
  getOpencodeModels,
  getOpencodeProviders,
  getOpencodeProviderModels,
  setOpencodeKey,
  testOpencodeKey,
  type OpencodeProvider,
  type OpencodeCatalogModel,
} from '@/api/settings'
import { setOpencodeModels } from '@/data/llmCatalog'

interface Props {
  open: boolean
  onClose: () => void
  /** 'configured' = server-configured providers; 'api_key' = bring your own key. */
  auth: 'configured' | 'api_key'
  /** Which settings scope a saved key belongs to (mirrors Settings → LLM). */
  scope: 'tenant' | 'user'
  /** Currently selected `provider/model` ('' = agent default). */
  value: string
  onSelect: (modelId: string) => void
  /** Title override (per-task rows). */
  title?: string
  /** Offer "Agent default" (clears the selection). */
  allowDefault?: boolean
  /** Label of that clearing row (per-task rows: "Use <base model>"). */
  defaultLabel?: string
}

function matches(q: string, ...fields: (string | undefined)[]) {
  if (!q) return true
  const needle = q.toLowerCase()
  return fields.some((f) => (f || '').toLowerCase().includes(needle))
}

function fmtCost(m: OpencodeCatalogModel) {
  if (m.free) return 'free'
  if (m.cost_in == null && m.cost_out == null) return ''
  return `$${m.cost_in ?? '?'} / $${m.cost_out ?? '?'} per 1M`
}

function fmtContext(n?: number) {
  if (!n) return ''
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M ctx` : `${Math.round(n / 1000)}k ctx`
}

export default function OpencodeModelPicker({
  open, onClose, auth, scope, value, onSelect, title, allowDefault = true, defaultLabel,
}: Props) {
  const [selected, setSelected] = useState(value)
  const [error, setError] = useState('')

  // --- configured mode ---
  const [serverModels, setServerModels] = useState<OpencodeCatalogModel[]>([])
  // --- api_key mode ---
  const [providers, setProviders] = useState<OpencodeProvider[]>([])
  const [keys, setKeys] = useState<Record<string, boolean>>({})
  const [provider, setProvider] = useState('')
  const [providerModels, setProviderModels] = useState<OpencodeCatalogModel[]>([])
  const [providerQuery, setProviderQuery] = useState('')
  const [onlyWithKey, setOnlyWithKey] = useState(false)
  const [keyDraft, setKeyDraft] = useState('')
  const [keyBusy, setKeyBusy] = useState(false)
  const [testing, setTesting] = useState(false)

  const [modelQuery, setModelQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // Escape closes THIS overlay only (see the header comment).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  // Load on open.
  useEffect(() => {
    if (!open) return
    setSelected(value)
    setError('')
    setModelQuery('')
    setKeyDraft('')
    setLoading(true)
    const initialProvider = value.includes('/') ? value.split('/')[0] : ''
    if (auth === 'configured') {
      getOpencodeModels()
        .then((res) => {
          setOpencodeModels(res.models || [])
          setServerModels(res.models || [])
          if (!res.status?.installed) setError('OpenCode CLI is not installed on the server (npm i -g opencode-ai).')
        })
        .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Could not load models'))
        .finally(() => setLoading(false))
    } else {
      getOpencodeProviders(scope)
        .then((res) => {
          setProviders(res.providers || [])
          setKeys(res.keys || {})
          const first =
            initialProvider
            || (res.providers || []).find((p) => res.keys?.[p.id])?.id
            || ''
          setProvider(first)
          if (!(res.providers || []).length) setError('OpenCode returned no provider catalog — is the CLI installed on the server?')
        })
        .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Could not load providers'))
        .finally(() => setLoading(false))
    }
    setTimeout(() => searchRef.current?.focus(), 50)
  }, [open, auth, scope]) // eslint-disable-line react-hooks/exhaustive-deps

  // Models of the chosen provider (api_key mode).
  useEffect(() => {
    if (!open || auth !== 'api_key' || !provider) {
      setProviderModels([])
      return
    }
    let alive = true
    setModelQuery('')
    getOpencodeProviderModels(provider)
      .then((ms) => { if (alive) setProviderModels(ms) })
      .catch(() => { if (alive) setProviderModels([]) })
    return () => { alive = false }
  }, [open, auth, provider])

  const providerSpec = providers.find((p) => p.id === provider)
  const hasKey = !!keys[provider]

  const visibleProviders = useMemo(
    () => providers.filter((p) =>
      (!onlyWithKey || keys[p.id]) && matches(providerQuery, p.name, p.id)),
    [providers, keys, onlyWithKey, providerQuery],
  )

  const visibleModels = useMemo(() => {
    const list = auth === 'configured' ? serverModels : providerModels
    return list.filter((m) => matches(modelQuery, m.name, m.id, m.provider))
  }, [auth, serverModels, providerModels, modelQuery])

  if (!open) return null

  async function saveKey(clear = false) {
    if (!provider) return
    const value = clear ? '-' : keyDraft.trim()
    if (!value) return
    setKeyBusy(true)
    try {
      const next = await setOpencodeKey(scope, provider, value)
      setKeys(next)
      setKeyDraft('')
      toast.success(clear ? `Removed the ${providerSpec?.name || provider} key` : `Saved the ${providerSpec?.name || provider} key`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not save the key')
    } finally {
      setKeyBusy(false)
    }
  }

  async function testKey() {
    if (!selected || !selected.startsWith(`${provider}/`)) {
      toast.error('Select one of this provider’s models first — the test sends it one tiny prompt.')
      return
    }
    setTesting(true)
    try {
      const r = await testOpencodeKey(scope, selected, keyDraft.trim())
      if (r.ok) toast.success(`${selected} answered — the key works.`)
      else toast.error(r.error || 'The key did not work')
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  const pane: React.CSSProperties = {
    display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
  }
  const listBox: React.CSSProperties = {
    flex: 1, minHeight: 0, overflowY: 'auto',
    border: '1px solid var(--color-border)', borderRadius: 8,
    background: 'var(--color-bg)',
  }

  const modelList = (
    <div style={listBox} role="listbox" aria-label="Models">
      {allowDefault && !modelQuery && (
        <ModelRow
          active={selected === ''}
          title={defaultLabel || 'Agent default'}
          sub={defaultLabel ? 'inherit this configuration’s model' : 'OpenCode’s own default model'}
          onClick={() => setSelected('')}
          onDouble={() => { onSelect(''); onClose() }}
        />
      )}
      {loading && <Empty text="Loading…" />}
      {!loading && visibleModels.length === 0 && (
        <Empty text={
          auth === 'api_key' && !provider
            ? 'Pick a provider on the left.'
            : modelQuery ? `No model matches “${modelQuery}”.` : 'No models.'
        } />
      )}
      {visibleModels.map((m) => (
        <ModelRow
          key={m.id}
          active={selected === m.id}
          title={m.name}
          sub={[m.id, fmtContext(m.context), fmtCost(m), m.status && m.status !== 'active' ? m.status : '']
            .filter(Boolean).join(' · ')}
          badge={m.free ? 'free' : m.reasoning ? 'reasoning' : ''}
          onClick={() => setSelected(m.id)}
          onDouble={() => { onSelect(m.id); onClose() }}
        />
      ))}
    </div>
  )

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        zIndex: 'calc(var(--z-overlay, 1000) + 20)' as unknown as number,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Choose an OpenCode model'}
        style={{
          width: auth === 'api_key' ? 'min(980px, 100%)' : 'min(640px, 100%)',
          height: 'min(680px, 92vh)',
          background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border)', borderRadius: 12,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{title || 'Choose an OpenCode model'}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {auth === 'configured'
                ? 'Models from the providers configured in OpenCode on the server.'
                : `Bring your own key — ${providers.length || 'all'} providers OpenCode supports. Keys are stored encrypted for the ${scope === 'user' ? 'personal' : 'organization'} scope and never shown again.`}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="modal-close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1, minHeight: 0, padding: 16, display: 'grid', gap: 16,
          gridTemplateColumns: auth === 'api_key' ? 'minmax(220px, 300px) 1fr' : '1fr',
        }}>
          {auth === 'api_key' && (
            <div style={pane}>
              <SearchBox
                inputRef={searchRef}
                value={providerQuery}
                onChange={setProviderQuery}
                placeholder={`Search ${providers.length} providers…`}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, margin: '8px 0', color: 'var(--color-text-secondary)' }}>
                <input type="checkbox" checked={onlyWithKey} onChange={(e) => setOnlyWithKey(e.target.checked)} />
                Only providers with a saved key
              </label>
              <div style={listBox} role="listbox" aria-label="Providers">
                {visibleProviders.length === 0 && !loading && <Empty text="No provider matches." />}
                {visibleProviders.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setProvider(p.id)}
                    title={p.key_only ? `${p.model_count} models` : `Needs ${p.env.join(', ')} — configure it in OpenCode on the server`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                      padding: '7px 10px', border: 'none', cursor: 'pointer',
                      borderBottom: '1px solid var(--color-border)',
                      background: provider === p.id ? 'var(--color-accent-subtle, rgba(59,130,246,0.12))' : 'transparent',
                      color: p.key_only ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      fontSize: 14,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </span>
                    {keys[p.id] && <KeyRound size={13} aria-label="key saved" style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />}
                    {p.configured_on_server && <Server size={13} aria-label="configured on server" style={{ flexShrink: 0 }} />}
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', flexShrink: 0 }}>{p.model_count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={pane}>
            {auth === 'api_key' && providerSpec && (
              <div style={{
                border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, marginBottom: 10,
                background: 'var(--color-bg)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: providerSpec.key_only ? 8 : 0 }}>
                  <strong style={{ fontSize: 14 }}>{providerSpec.name}</strong>
                  <span style={{ fontSize: 13, color: hasKey ? 'var(--color-success, #16a34a)' : 'var(--color-text-secondary)' }}>
                    {hasKey ? '● key saved' : '○ no key yet'}
                  </span>
                  {providerSpec.doc && (
                    <a href={providerSpec.doc} target="_blank" rel="noreferrer"
                      style={{ marginLeft: 'auto', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      docs <ExternalLink size={11} />
                    </a>
                  )}
                </div>
                {providerSpec.key_only ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="password"
                      className="input"
                      autoComplete="off"
                      value={keyDraft}
                      onChange={(e) => setKeyDraft(e.target.value)}
                      placeholder={hasKey ? 'Paste a new key to replace the saved one' : `${providerSpec.env[0] || 'API key'}`}
                      style={{ flex: 1, minWidth: 200 }}
                    />
                    <Button size="sm" variant="primary" onClick={() => saveKey(false)} disabled={keyBusy || !keyDraft.trim()}>
                      {hasKey ? 'Replace' : 'Save key'}
                    </Button>
                    {hasKey && (
                      <Button size="sm" variant="ghost" onClick={() => saveKey(true)} disabled={keyBusy}>
                        Remove
                      </Button>
                    )}
                    <Button size="sm" variant="secondary" onClick={testKey} disabled={testing || (!hasKey && !keyDraft.trim())}
                      title="Sends the selected model one tiny prompt with this key (a few tokens on your account)">
                      {testing ? 'Testing…' : 'Test'}
                    </Button>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--color-warning, #b45309)', marginTop: 6 }}>
                    {providerSpec.name} needs more than a key ({providerSpec.env.join(', ')}). Configure it in
                    OpenCode on the server and use the “OpenCode providers” mode instead.
                  </div>
                )}
              </div>
            )}
            <SearchBox
              inputRef={auth === 'configured' ? searchRef : undefined}
              value={modelQuery}
              onChange={setModelQuery}
              placeholder={
                auth === 'configured'
                  ? `Search ${serverModels.length} models…`
                  : provider ? `Search ${providerModels.length} ${providerSpec?.name || provider} models…` : 'Search models…'
              }
            />
            <div style={{ height: 8 }} />
            {modelList}
          </div>
        </div>

        {error && (
          <div style={{ margin: '0 16px 8px', fontSize: 13, color: 'var(--color-error, #dc2626)' }}>{error}</div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected ? <>Selected: <code>{selected}</code></> : `Selected: ${defaultLabel || 'agent default'}`}
            {auth === 'api_key' && selected && !keys[selected.split('/')[0]] && (
              <span style={{ color: 'var(--color-warning, #b45309)' }}> — no key saved for this provider yet</span>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => { onSelect(selected); onClose() }}
            disabled={auth === 'api_key' && !selected && !allowDefault}
          >
            <Check size={14} style={{ marginRight: 4 }} /> Use this model
          </Button>
        </div>
      </div>
    </div>
  )
}

function SearchBox({
  value, onChange, placeholder, inputRef,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  inputRef?: React.RefObject<HTMLInputElement | null>
}) {
  return (
    <div style={{ position: 'relative' }}>
      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
      <input
        ref={inputRef}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ paddingLeft: 30, width: '100%' }}
      />
    </div>
  )
}

function ModelRow({
  active, title, sub, badge, disabled, onClick, onDouble,
}: {
  active: boolean
  title: string
  sub?: string
  badge?: string
  disabled?: boolean
  onClick: () => void
  onDouble: () => void
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      disabled={disabled}
      onClick={onClick}
      onDoubleClick={disabled ? undefined : onDouble}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        padding: '8px 10px', border: 'none', borderBottom: '1px solid var(--color-border)',
        background: active ? 'var(--color-accent-subtle, rgba(59,130,246,0.12))' : 'transparent',
        color: disabled ? 'var(--color-text-secondary)' : 'var(--color-text-primary)',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: active ? 600 : 400 }}>{title}</span>
        {sub && (
          <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sub}
          </span>
        )}
      </span>
      {badge && (
        <span className="badge badge-info" style={{ fontSize: 13, flexShrink: 0 }}>{badge}</span>
      )}
      {active && <Check size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />}
    </button>
  )
}

function Empty({ text }: { text: string }) {
  return <div style={{ padding: 16, fontSize: 14, color: 'var(--color-text-secondary)' }}>{text}</div>
}
